import {
  ComponentPropsWithRef,
  forwardRef,
  ForwardedRef,
  useState,
} from "react";
import { t } from "i18next";
import { tv } from "tailwind-variants";

import FileInput from "@/components/FileInput";
import { Button } from "@/components/Button";

const styles = tv({
  slots: {
    base: "flex flex-col gap-y-10 md:flex-row md:items-end md:gap-x-20",
    previewImg: "bg-gray-200 object-scale-down p-1",
  },
  variants: {
    policy: {
      true: {
        base: "flex w-full flex-grow",
      },
    },
  },
});

interface IImgUploadProps extends ComponentPropsWithRef<"input"> {
  image?: string;
  type: "logo" | "banner" | "policy";
  helperText?: string;
  testId?: string;
  onClear?: () => void;
}

const ImageUpload = forwardRef(
  (
    { image, type, helperText, testId, onClear, ...rest }: IImgUploadProps,
    ref: ForwardedRef<HTMLInputElement | null>,
  ) => {
    const { base, previewImg } = styles({ policy: type === "policy" });

    const [previewImage, setPreviewImage] = useState<
      string | ArrayBuffer | null
    >(image as string);

    const [resetImage, setResetImage] = useState<boolean>(false);
    const toggleReset = () => setResetImage(resetImage => !resetImage);

    const handleFileUpload = (file?: File) => {
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) setPreviewImage(e.target.result);
      };
      if (file) reader.readAsDataURL(file);
    };

    const handleUnsetImage = () => {
      toggleReset();
      setPreviewImage(null);
      onClear && onClear();
    };

    const getFileInputVariant = () =>
      helperText ? "error" : previewImage ? "success" : undefined;

    const renderImage = () => (
      <img
        src={previewImage as string}
        width={type === "banner" ? "210px" : "180px"}
        height={type === "banner" ? "115px" : "60px"}
        className={previewImg()}
      />
    );

    const renderRemoveImageLink = () => {
      return (
        <Button
          type="button"
          variant="link-light"
          width={"unset"}
          onClick={handleUnsetImage}>
          {t("components.imageUpload.removeLink")}
        </Button>
      );
    };

    return (
      <div className={base()} data-testid={testId}>
        <FileInput
          ref={ref}
          reset={resetImage}
          helperText={helperText}
          variant={getFileInputVariant()}
          handleFileUpload={handleFileUpload}
          fullWidth={type === "policy"}
          {...rest}
        />

        {previewImage && type !== "policy" && (
          <>
            <div>{renderImage()}</div>
            <div>{renderRemoveImageLink()}</div>
          </>
        )}
      </div>
    );
  },
);

export default ImageUpload;
